import type { Request, Response } from 'express';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo } from 'react';
import { handleMenuData } from '../bff/handleMenuData';
import { TrackPageViewContainer } from '../common/containers/TrackPageViewContainer';
import { getDehydratedState } from '../common/getDehydrateState';
import { t } from '../common/getTranslations';
import { TabPositionSticky } from '../common/presentational/TabPositionSticky';
import { replaceWhiteSpaceToUnderbar } from '../common/replaceWhiteSpaceToUnderbar.function';
import { requestContext } from '../common/requestContext';
import { twMerge } from '../common/twMerge';
import useTypedSelector from '../common/useTypedSelector';
import { ButtonV2Variant } from '../design-system/atoms/ButtonV2/ButtonV2.enum';
import { StaticImageType } from '../design-system/atoms/StaticImage/StaticImageType.enum';
import { Layout } from '../design-system/layout/Layout';
import { SubTabItemType } from '../design-system/organisms/SubTabItem/SubTabItemType.interface';
import { TasteTab } from '../design-system/organisms/TasteTab/TasteTab';
import { useDialog } from '../dialog/useDialog.hook';
import PageLayout from '../domain/landing/presentational';
import { BOTTOM_LINE_STATIC_LAYOUT_TYPE_LIST, MENU_UID_PREFER } from '../domain/menu/constant';
import { getMenuFromStore } from '../domain/menu/util/getMenuFromStore';
import { addError } from '../errors/ErrorSlice';
import { PagewebErrorType } from '../errors/PagewebErrorType.enum';
import { thunkSerializeError } from '../errors/thunkSerializeError.function';
import { CommonMobileHeaderType } from '../header/constants';
import { aggregateAndSendLog } from '../logging/aggregateAndSendLog';
import { LOG_CLICK_EVENT } from '../logging/logEvent';
import { PageViewCustomPropsDataKey } from '../logging/PageViewCustomPropsDataKey.enum';
import { putTiaraObjectProps } from '../logging/putTiaraObjectProps';
import { LOCAL_STORAGE_VISIT_WELCOME_PAGE, SESSION_STORAGE_WELCOME_POPUP } from '../popup/constants';
import { nextReduxWrapper } from '../state/store';
import { usePopupQuery } from '../wrapper';
function Index({
  layoutId,
  screenUid
}: {
  layoutId?: string;
  screenUid?: number;
  menuUid?: number;
}) {
  const router = useRouter();
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  const {
    subtabMenuMap,
    selectedSubtabMenu,
    selectedSubtab,
    selectedLandingType
  } = useTypedSelector(state => {
    const subtabMenuMap = state.menu.entities.subtabMenuMap;
    const selectedSubtabMenu = subtabMenuMap[MENU_UID_PREFER];
    const subtabMap = state.menu.entities.subtabMap;
    const selectedSubtab = screenUid !== undefined ? subtabMap[screenUid] : undefined;
    const selectedLandingType = selectedSubtab ? state.menu.entities.subtabMap[selectedSubtab.uid].landingType : undefined;
    return {
      subtabMenuMap,
      selectedSubtabMenu,
      selectedSubtab,
      selectedLandingType
    };
  });
  const subtabMenuItems: SubTabItemType[] = useMemo(() => {
    const nextMenuUid = subtabMenuMap?.[MENU_UID_PREFER]?.uid;
    return selectedSubtabMenu?.screenList.map(screen => ({
      name: screen.title,
      param: String(screen.uid),
      href: `/menu/${nextMenuUid}/screen/${screen.uid}`,
      ageGrade: screen.ageGrade,
      icon: screen.icon
    }), []) || [];
  }, [selectedSubtabMenu?.screenList, subtabMenuMap]);
  const {
    DialogWrapper,
    Dialog,
    showDialog,
    hideDialog
  } = useDialog({
    isMobile: requestContext.requestInfo.display.isMobile,
    openWhenMount: false
  });
  const userUid = useTypedSelector(state => state.user.user?.uid);
  const user = useTypedSelector(state => state.user.user);
  const {
    data
  } = usePopupQuery(undefined, {
    gcTime: Infinity,
    enabled: !!user
  });
  useEffect(() => {
    if (!data || !data.popup || !userUid) return;
    const sessionWelcomePopup = window.sessionStorage.getItem(SESSION_STORAGE_WELCOME_POPUP);
    const localStorageWelcomePopup = window.localStorage.getItem(LOCAL_STORAGE_VISIT_WELCOME_PAGE);
    if (data.popup?.useWelcome && sessionWelcomePopup !== String(userUid) && localStorageWelcomePopup !== String(userUid)) {
      window.sessionStorage.setItem(SESSION_STORAGE_WELCOME_POPUP, String(userUid));
      showDialog();
    }
  }, [data, showDialog, userUid]);
  return <div className="flex flex-col w-full grow">
      {!!selectedSubtabMenu && screenUid !== undefined && <TabPositionSticky depth={2} isMobile={isMobile} isWebView={isWebView}>
          <TasteTab key={layoutId} tabItems={subtabMenuItems} currentTabItemParam={String(screenUid)} isMobile={isMobile} hasBottomLine={isMobile && selectedLandingType ? BOTTOM_LINE_STATIC_LAYOUT_TYPE_LIST.includes(selectedLandingType) : false} className="before:absolute before:bottom-0 before:left-0 before:z-[-1] before:h-57pxr before:w-full before:bg-theme-bg-base" onClickTab={(_, e) => LOG_CLICK_EVENT('추천_서브탭_클릭', e)} />
        </TabPositionSticky>}
      <TrackPageViewContainer trackObj={{
      shouldSendUsagePage: true,
      section: '추천',
      page: `추천_${replaceWhiteSpaceToUnderbar(selectedSubtab?.title)}`,
      actionType: 'Pageview',
      actionName: `추천_${replaceWhiteSpaceToUnderbar(selectedSubtab?.title)}_화면`,
      page_meta: {
        id: String(screenUid),
        type: 'screenId'
      },
      customProps: {
        user_action: 'pv',
        tab_type: 'section'
      }
    }} customPropsDataKeyList={[PageViewCustomPropsDataKey.PageCategoryFilter, PageViewCustomPropsDataKey.PageGenreFilter, PageViewCustomPropsDataKey.PageBmFilter, PageViewCustomPropsDataKey.PageDay, PageViewCustomPropsDataKey.PageSort, PageViewCustomPropsDataKey.Completed, PageViewCustomPropsDataKey.TabType]} />
      {layoutId && <PageLayout id={layoutId} className={twMerge(!isMobile && 'pt-32pxr')} />}

      <DialogWrapper {...putTiaraObjectProps({
      page_meta: {
        id: '0',
        type: 'screenId'
      },
      click: {
        layer1: '웰컴팝업',
        imp_id: 'popup_w',
        imp_provider: 'service'
      },
      customProps: {
        landing_path: '/welcome',
        popup_type: '웰컴팝업'
      },
      viewimp_contents: {
        imp_id: 'popup_w',
        imp_provider: 'service',
        imp_type: 'vimp',
        layer1: '웰컴팝업'
      }
    })} onInView={target => {
      aggregateAndSendLog({
        targetElem: target as HTMLElement,
        actionType: 'ViewImp',
        actionName: '팝업_노출',
        customProps: {
          user_action: 'imp'
        }
      });
    }}>
        <Dialog.Dimmed />
        <Dialog.List>
          <Dialog.Image imageType={StaticImageType.ModalCashStatic} />
          <Dialog.Title>{t('welcome_popup_title')}</Dialog.Title>
          <Dialog.Label>{t('welcome_popup_detail')}</Dialog.Label>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={e => {
          LOG_CLICK_EVENT('웰컴팝업_닫기_클릭', e, () => {
            hideDialog();
          });
        }}>
            {t('common_close_button')}
          </Dialog.Button>
          <Dialog.Button {...putTiaraObjectProps({
          click: {
            copy: '지금 보러 가기'
          }
        })} onClick={e => {
          LOG_CLICK_EVENT('웰컴팝업_확인_클릭', e, () => {
            router.push('/welcome');
          });
        }} variant={ButtonV2Variant.Primary}>
            {t('welcome_popup_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </div>;
}
Index.getInitialProps = nextReduxWrapper.getInitialPageProps(store => async context => {
  const menu = await getMenuFromStore({
    store,
    req: context?.req as Request,
    res: context?.res as Response
  });
  const validScreenUid = menu.subtabMenuList.find(subtabMenu => subtabMenu.uid === MENU_UID_PREFER)?.screenList[0].uid;
  if (menu.error?.type === PagewebErrorType.NicolasApiStatusError) {
    store.dispatch(addError({
      error: menu.error
    }));
    return;
  }
  if (validScreenUid === undefined) {
    return {
      notFound: true
    };
  }
  try {
    const {
      queryClient,
      ...result
    } = await handleMenuData({
      store,
      menu,
      menuUid: MENU_UID_PREFER,
      screenUid: validScreenUid,
      ssrRequest: context?.req as Request,
      query: context.query,
      cookie: context?.req?.headers.cookie
    });
    return {
      ...result,
      ...(queryClient && {
        ...getDehydratedState(queryClient)
      })
    };
  } catch (error) {
    store.dispatch(addError({
      error: thunkSerializeError(error)
    }));
    return {};
  }
});
Index.getLayout = function getLayout(page: ReactElement) {
  return <Layout title={page.props.metaInfo?.title || t('mainhome_tab_recommendation')} mobileLayoutConfig={{
    shouldShowHeader: true,
    shouldShowGnb: true,
    shouldShowFooter: true,
    headerType: CommonMobileHeaderType.MainAndSub
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true
  }} metaInfo={page.props.metaInfo}>
      {page}
    </Layout>;
};
export default Index;